import { CodeOffOutlined, CodeOutlined } from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  Button,
  Card,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import mermaid from "mermaid";
import { useEffect, useRef, useState } from "react";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import MermaidChartEditor from "./MermaidChartEditor";
import MermaidChart from "./MermaidChart";

// Initialize mermaid with configuration
mermaid.initialize({
  startOnLoad: true,
  theme: "default",
  securityLevel: "loose",
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: "basis",
  },
});

const MermaidChartViewer = ({ chart, title, onChartChange }) => {
  const chartRef = useRef(null);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedChart, setEditedChart] = useState(chart);
  const [lastValidChart, setLastValidChart] = useState(chart);

  useEffect(() => {
    setEditedChart(chart);
    setLastValidChart(chart);
  }, [chart]);

  useEffect(() => {
    const renderChart = async () => {
      if (!chartRef.current || !editedChart) return;

      try {
        setError(null);
        const uniqueId = `mermaid-${Date.now()}`;

        // Generate the chart
        const { svg } = await mermaid.render(uniqueId, editedChart);
        chartRef.current.innerHTML = svg;
        setLastValidChart(editedChart);
      } catch (err) {
        console.error("Mermaid rendering error:", err);
        setError(err.message);
      }
    };

    renderChart();
  }, [editedChart]);

  const handleApplyChanges = () => {
    if (onChartChange) {
      onChartChange(editedChart);
    }
    setIsEditing(false);
  };

  const handleResetChanges = () => {
    setEditedChart(lastValidChart);
    setError(null);
  };

  const downloadAsSVG = () => {
    const svgElement = chartRef.current?.querySelector("svg");
    if (!svgElement) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `${title || "mermaid-chart"}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadAsPNG = () => {
    const svgElement = chartRef.current?.querySelector("svg");
    if (!svgElement) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    const svgRect = svgElement.getBoundingClientRect();
    canvas.width = svgRect.width * 2;
    canvas.height = svgRect.height * 2;

    img.onload = () => {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${title || "mermaid-chart"}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    };

    img.src =
      "data:image/svg+xml;base64," +
      btoa(unescape(encodeURIComponent(svgData)));
  };

  const copyToClipboard = async () => {
    const svgElement = chartRef.current?.querySelector("svg");
    if (!svgElement) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    const svgRect = svgElement.getBoundingClientRect();
    canvas.width = svgRect.width * 2;
    canvas.height = svgRect.height * 2;

    img.onload = async () => {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(async (blob) => {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({ "image/png": blob }),
          ]);
          alert("Chart copied to clipboard!");
        } catch (err) {
          console.error("Failed to copy:", err);
          alert("Failed to copy to clipboard");
        }
      });
    };

    img.src =
      "data:image/svg+xml;base64," +
      btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <Box
      sx={{
        background: "#f8fafc",
        flexGrow: 1,
        overflow: "hidden",
        display: "flex",
      }}
    >
      {" "}
      {isEditing && (
        <Box sx={{ flexGrow: 1 }}>
          <MermaidChartEditor
            onApplyChanges={handleApplyChanges}
            onReset={handleResetChanges}
            editedChart={editedChart}
            setEditedChart={setEditedChart}
            onClose={() => setIsEditing(false)}
          />
        </Box>
      )}
      <Box
        sx={{
          // background: "yellow",
          flexGrow: 1,
          flexShrink: 1,
          p: 2,
          overflowY: "auto",
        }}
      >
        <MermaidChart chart={chart} title={title} />
      </Box>
    </Box>
  );

  return (
    <Box>
      <Flx jw jb ac sx={{ mb: 1 }}>
        {title && (
          <Txt fontSize={16} sx={{ lineHeight: 1 }} bold center>
            {title}
          </Txt>
        )}
        <Flx ac g={0.5}>
          <Tooltip title={isEditing ? "View Mode" : "Edit Syntax"}>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                isEditing ? (
                  <CodeOffOutlined className="thin" fontSize="small" />
                ) : (
                  <CodeOutlined className="thin" fontSize="small" />
                )
              }
              onClick={() => setIsEditing(!isEditing)}
              //   sx={{
              //     border: `1px solid ${grey[300]}`,
              //     bgcolor: isEditing ? grey[200] : "transparent",
              //   }}
            >
              Edit
            </Button>
          </Tooltip>
          <Tooltip title="Download as SVG">
            <Button
              size="small"
              var
              onClick={downloadAsSVG}
              disabled={!!error}
              startIcon={<DownloadIcon fontSize="small" />}
            >
              SVG
            </Button>
          </Tooltip>
          <Tooltip title="Download as PNG">
            <Button
              size="small"
              onClick={downloadAsPNG}
              disabled={!!error}
              startIcon={<DownloadIcon fontSize="small" />}
            >
              PNG
            </Button>
          </Tooltip>
          <Tooltip title="Copy to clipboard">
            <IconButton
              size="small"
              onClick={copyToClipboard}
              disabled={!!error}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Flx>
      </Flx>

      <Flx g={3}>
        {isEditing && (
          <MermaidChartEditor
            onApplyChanges={handleApplyChanges}
            onReset={handleResetChanges}
            editedChart={editedChart}
            setEditedChart={setEditedChart}
            onClose={() => setIsEditing(false)}
          />
        )}
        {error ? (
          <Paper
            sx={{ p: 2, bgcolor: "error.light", color: "error.contrastText" }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, flexGrow: 1, mb: 1 }}
            >
              Error rendering chart:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {error}
            </Typography>
          </Paper>
        ) : (
          <Card
            variant="outlined"
            sx={{
              border: `1px solid ${grey[300]}`,
              boxShadow: "none",
              p: 2,
              flexGrow: 1,
              overflow: "auto",
              "& .mermaid": {
                display: "flex",
                justifyContent: "center",
              },
            }}
          >
            <div className="mermaid" ref={chartRef} />
          </Card>
        )}
      </Flx>
    </Box>
  );
};

// Demo component with multiple chart examples

export default MermaidChartViewer;
